import * as R from 'ramda'

import {
  assignWith,
  isFunction,
  mapValues,
} from 'lodash'

import { Dispatch } from "redux";

import { generateReducer, generateActions } from './itemReducer'

import { mergeStates } from '../helpers/Doc/documentUserState'
import offlineProgressSync from '../helpers/OfflineProgressSync';

const itemKey = 'documentUserState'
const endpoint = 'api/v2/document_user_states/'

export function generateDocumentUserStateActions(id: ItemId) {
  const documentUserStateActions = generateActions<DocumentUserState, DocumentUserStateExtraData>(itemKey, id, endpoint, {}, {editDataMergeFunction: editDataMergeFunction})

  return {
    startNewDocumentUserState: documentUserStateActions.startNewItem,
    cancelNewDocumentUserState: documentUserStateActions.cancelNewItem,
    startEditDocumentUserState: documentUserStateActions.startEditItem,
    cancelEditDocumentUserState: documentUserStateActions.cancelEditItem,
    setAllDataWithFunctionDocumentUserState: documentUserStateActions.setAllDataWithFunctionItem,
    setSyncDataDocumentUserState: documentUserStateActions.setSyncDataItem,
    setSyncDataWithFunctionDocumentUserState: documentUserStateActions.setSyncDataWithFunctionItem,
    setEditDataDocumentUserState: documentUserStateActions.setEditDataItem,
    setEditDataWithFunctionDocumentUserState: documentUserStateActions.setEditDataWithFunctionItem,
    resetEditDataDocumentUserState: documentUserStateActions.resetEditDataItem,
    setExtraDataDocumentUserState: documentUserStateActions.setExtraDataItem,
    setExtraDataWithFunctionDocumentUserState: documentUserStateActions.setExtraDataWithFunctionItem,
    resetExtraDataDocumentUserState: documentUserStateActions.resetExtraDataItem,
    loadDocumentUserState: documentUserStateActions.loadItem,
    createDocumentUserState: documentUserStateActions.createItem,
    updateDocumentUserState: documentUserStateActions.updateItem,
    destroyDocumentUserState: documentUserStateActions.destroyItem,
    loadIfNeededDocumentUserState: documentUserStateActions.loadIfNeededItem,
    documentUserStateFromState: documentUserStateActions.itemFromState,
    documentUserStateSyncDataFromState: documentUserStateActions.itemSyncDataFromState,
    documentUserStateSyncMetaFromState: documentUserStateActions.itemSyncMetaFromState,
    documentUserStateEditDataFromState: documentUserStateActions.itemEditDataFromState,
    documentUserStateMergedDataFromState: documentUserStateActions.itemMergedDataFromState,
    documentUserStateExtraDataFromState: documentUserStateActions.itemExtraDataFromState,
    addStateDocumentUserState: (state: UserStateStripped | UserStateStrippedFunction, callback?: () => void) => addStateDocumentUserState(id, state, callback)
  }
}

export const documentUserStateCollectionReducer = generateReducer<DocumentUserState, DocumentUserStateExtraData>(itemKey);

function editDataMergeFunction(dusOld: DocumentUserState, dusNew: Partial<DocumentUserState>) {
  return assignWith({}, dusOld, dusNew, (valueDataOld, valueDataNew, key) => {
    if (key === 'state') {
      return mergeStates(valueDataOld, valueDataNew)
    } else {
      return valueDataNew || valueDataOld
    }
  })
}

export function addStateDocumentUserState(id: ItemId, state: UserStateStripped | UserStateStrippedFunction, callback?: () => void) {
  return (dispatch: Dispatch, getState: () => ReducerState) => {
    const documentUserStateActions = generateDocumentUserStateActions(id)

    let mergedDocumentUserState = documentUserStateActions.documentUserStateMergedDataFromState(getState())
    let editDocumentUserState = documentUserStateActions.documentUserStateEditDataFromState(getState()) || {}

    let mergedState = mergedDocumentUserState.state || {}
    let editState = editDocumentUserState.state || {}

    const getStateFunction = (uid: string) => mapValues(mergedState[uid], (valueObject) => valueObject.value);

    let stateToUse = typeof state === 'function' ? state(getStateFunction) : state

    let newState = R.mapObjIndexed((nodeData, uid, obj1) => {
      return R.mapObjIndexed((value, key, obj2) => {
        let newValue = isFunction(value) ? value(R.path([uid, key, 'value'], mergedState)) : value
        return {value: newValue, updatedAt: (new Date).getTime()}
      }, nodeData)
    }, stateToUse)

    const finalState = mergeStates(editState, newState);
    
    // Check if we should queue for offline sync
    if (offlineProgressSync.shouldQueue()) {
      console.log('Offline: Queuing document user state update for later sync');
      
      dispatch(documentUserStateActions.setEditDataDocumentUserState({state: finalState}))
      
      const documentId = typeof id === 'string' && id.includes('/') ? id.split('/')[0] : id;
      const userId = typeof id === 'string' && id.includes('/') ? id.split('/')[1] : mergedDocumentUserState.userId;

      offlineProgressSync.queueDocumentUserStateUpdate(documentId, userId, finalState, endpoint);
      
      if (callback) {
        callback()
      }
      return;
    }

    // Normal online behavior
    dispatch(documentUserStateActions.setEditDataDocumentUserState({state: finalState}))
    dispatch(documentUserStateActions.updateDocumentUserState() as any)
    const documentId = typeof id === 'string' && id.includes('/') ? id.split('/')[0] : id;
    const userId = typeof id === 'string' && id.includes('/') ? id.split('/')[1] : mergedDocumentUserState.userId;
    offlineProgressSync.cacheDocumentUserState(documentId, userId, { state: mergeStates(mergedState, finalState) }).catch(() => {})

    if (callback) {
      callback()
    }
  }
}
